import React from 'react';
import styled from 'styled-components';

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const StatCard = styled.div`
  background: ${({ isLightMode }) =>
    isLightMode ? "#FFFFFF" : "linear-gradient(135deg, #2D354A, #191B27)"};
  padding: 25px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ isLightMode }) => (isLightMode ? "#2D354A" : "#0093FF")};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 147, 255, 0.2);
  }
`;

const StatValue = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 32px;
  color: ${({ isLightMode }) => (isLightMode ? "#191B27" : "#00D160")};
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: ${({ isLightMode }) => (isLightMode ? "#191B27" : "#DCE4F2")};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StatSubtext = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: ${({ isLightMode }) => (isLightMode ? "#2D354A" : "#8B8FA3")};
  margin-top: 4px;
`;

const formatCompactNumber = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
};

const formatCompactCurrency = (value) => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  return `$${value.toFixed(0)}`;
};


function StatisticsBar({ className, data, isLightMode }) {
  const isPrint = window.matchMedia("print").matches;
 const calculateStats = () => {
  if (!data || data.length === 0) {
    return {
      totalMetricTons: 0,
      avgValPerKg: 0,
      totalValue: 0
    };
  }

  // Sum metric tons and total value
  const totalMetricTons = data.reduce(
    (sum, row) => sum + (row.metricTons || 0),
    0
  );

  const totalValue = data.reduce(
    (sum, row) => sum + (row.totalValue || 0),
    0
  );

  // Convert metric tons → kilograms (1 ton = 1000 kg)
  const totalKg = totalMetricTons * 1000;

  // Weighted Val/KG = totalValue / totalKg
  const avgValPerKg = totalKg > 0 ? totalValue / totalKg : 0;

  return {
    totalMetricTons,
    avgValPerKg,
    totalValue
  };
};


  const stats = calculateStats();

  const formatNumber = (num, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
  <div className={`statistics-bar ${className || ""}`}>
    <StatsContainer>
      <StatCard isLightMode={isLightMode} className="stat-box border-green">
        <StatValue isLightMode={isLightMode}>
          {isPrint
  ? formatCompactNumber(stats.totalMetricTons)
  : formatNumber(stats.totalMetricTons, 2)}
        </StatValue>
        <StatLabel isLightMode={isLightMode}>Metric Tons</StatLabel>
        <StatSubtext isLightMode={isLightMode}>Total Weight Imported</StatSubtext>
      </StatCard>

      <StatCard isLightMode={isLightMode} className="stat-box border-blue">
        <StatValue isLightMode={isLightMode}>
          {isPrint
  ? `$${stats.avgValPerKg.toFixed(2)}`
  : `$${formatNumber(stats.avgValPerKg, 2)}`}
        </StatValue>
        <StatLabel isLightMode={isLightMode}>Val / KG ($)</StatLabel>
        <StatSubtext isLightMode={isLightMode}>Average Value per Kilogram</StatSubtext>
      </StatCard>

      <StatCard isLightMode={isLightMode} className="stat-box border-green">
        <StatValue isLightMode={isLightMode}>
          {isPrint
  ? formatCompactCurrency(stats.totalValue)
  : formatCurrency(stats.totalValue)}
        </StatValue>
        <StatLabel isLightMode={isLightMode}>Total Value</StatLabel>
        <StatSubtext isLightMode={isLightMode}>Total Calculated Value</StatSubtext>
      </StatCard>
    </StatsContainer>
  </div>
);

}

export default StatisticsBar;
